import React from 'react'
import {
  PaymentQueueOrderType,
  PaymentQueueStatus,
  IPaymentQueueWithExternalSource,
  ICompletePaymentDetails,
  PermissionNames,
} from '@getgreenline/homi-shared'
import { Tooltip, Button, Modal } from 'antd'
import ParkedSaleStatusButton from './ParkedSaleStatusButton'
import { ParkedSaleStore } from './ParkedSaleStore'
import { inject, observer } from 'mobx-react'
import { CurrentCompanyStore } from '../../../../stores/CurrentCompanyStore'
import { CurrentUserStore } from '../../../../stores/CurrentUserStore'
import { HelpLinks } from '../../../../constants/ExternalLinks'

export enum ParkedSaleButtonStatus {
  ACTIVE = 'active',
  DISABLED = 'disabled',
  DEFAULT = 'default',
  NOT_CLICKABLE = 'notClickable',
}
interface Props {
  parkedSaleStore: ParkedSaleStore
  setValidationModalOpen: (isValidationModalOpen: boolean) => void
  currentCompanyStore?: CurrentCompanyStore
  currentUserStore?: CurrentUserStore
}

const confirmModal = (
  parkedSaleModalQueue: IPaymentQueueWithExternalSource | undefined,
  changeStatus: (buttonStatus: PaymentQueueStatus) => Promise<void>,
) => {
  const currentStatus = parkedSaleModalQueue?.status
  if (!currentStatus) return

  Modal.confirm({
    type: 'warning',
    title: 'Complete sale',
    icon: 'warning',
    content: (
      <div>
        <p>The current order status is:</p>

        <div className='mb-3 text-center' style={{ marginRight: 38 }}>
          <ParkedSaleStatusButton
            status={currentStatus}
            buttonStatus={ParkedSaleButtonStatus.NOT_CLICKABLE}
          />
        </div>

        <p>
          Customers may not get notified if you skip to Completed status. Are you sure you want to
          complete the sale?
        </p>
      </div>
    ),
    okText: 'Complete',
    onOk: () => changeStatus(PaymentQueueStatus.COMPLETED),
  })
}

const showConditionalCompleteButton: (
  buttonStatus: PaymentQueueStatus,
  currentParkedSaleButtonStatus: ParkedSaleButtonStatus,
  completedPayment: ICompletePaymentDetails | undefined,
  parkedSaleModalQueue: IPaymentQueueWithExternalSource | undefined,
  changeStatus: (buttonStatus: PaymentQueueStatus) => Promise<void>,
) => JSX.Element = (
  buttonStatus,
  currentParkedSaleButtonStatus,
  completedPayment,
  parkedSaleModalQueue,
  changeStatus,
) => {
  const toolTipMessage = 'To complete this parked sale, make a payment in the POS'

  return (
    <Tooltip key={buttonStatus} title={completedPayment ? '' : toolTipMessage}>
      <div>
        <ParkedSaleStatusButton
          status={buttonStatus}
          buttonStatus={
            completedPayment ? currentParkedSaleButtonStatus : ParkedSaleButtonStatus.DISABLED
          }
          onClick={() => {
            if (!completedPayment) return
            confirmModal(parkedSaleModalQueue, changeStatus)
          }}
        />
      </div>
    </Tooltip>
  )
}

export const ParkedSaleStatusButtonLists = inject(
  'currentCompanyStore',
  'currentUserStore',
)(
  observer(
    ({ parkedSaleStore, setValidationModalOpen, currentCompanyStore, currentUserStore }: Props) => {
      const { isDashboardBarcodeValidationEnabled, parkedSaleModalQueue, setParkedSaleStatus } =
        parkedSaleStore
      const { completedPayment, status, orderType, externalSourceName } = parkedSaleModalQueue || {}
      const canEditSales = currentUserStore!.hasPermissionAtEntity(
        PermissionNames.CAN_EDIT_SALES,
        currentCompanyStore!.company!.id,
      )

      const getAvailableButtons: () => PaymentQueueStatus[] = () => {
        switch (true) {
          case status === PaymentQueueStatus.CANCELLED:
            return [PaymentQueueStatus.CANCELLED]
          case status === PaymentQueueStatus.COMPLETED:
            return [PaymentQueueStatus.COMPLETED]
          case orderType === PaymentQueueOrderType.DELIVERY:
            return [
              PaymentQueueStatus.PENDING,
              PaymentQueueStatus.READY_FOR_DELIVERY,
              PaymentQueueStatus.OUT_FOR_DELIVERY,
              PaymentQueueStatus.COMPLETED,
            ]
          default:
            return [
              PaymentQueueStatus.PENDING,
              PaymentQueueStatus.READY_FOR_PICKUP,
              PaymentQueueStatus.COMPLETED,
            ]
        }
      }

      const changeStatus = async (buttonStatus: PaymentQueueStatus) => {
        if (!parkedSaleModalQueue) {
          return
        }
        await setParkedSaleStatus(parkedSaleModalQueue, buttonStatus)
      }

      const parkedSaleButtonStatus: (buttonStatus: PaymentQueueStatus) => ParkedSaleButtonStatus = (
        buttonStatus,
      ) => {
        switch (true) {
          case buttonStatus === PaymentQueueStatus.COMPLETED &&
            status === PaymentQueueStatus.COMPLETED:
          case buttonStatus === PaymentQueueStatus.COMPLETED && !completedPayment:
          case buttonStatus === PaymentQueueStatus.CANCELLED:
          case buttonStatus === PaymentQueueStatus.OUT_FOR_DELIVERY &&
            !canEditSales &&
            !!completedPayment:
          case buttonStatus === PaymentQueueStatus.READY_FOR_PICKUP &&
            !canEditSales &&
            !!completedPayment:
            return ParkedSaleButtonStatus.DISABLED
          case buttonStatus === status:
            return ParkedSaleButtonStatus.ACTIVE
          default:
            return ParkedSaleButtonStatus.DEFAULT
        }
      }

      const defaultParkedSaleButton: (
        buttonStatus: PaymentQueueStatus,
        currentParkedSaleButtonStatus: ParkedSaleButtonStatus,
      ) => JSX.Element = (buttonStatus, currentParkedSaleButtonStatus) => (
        <ParkedSaleStatusButton
          key={`key${buttonStatus}`}
          status={buttonStatus}
          buttonStatus={currentParkedSaleButtonStatus}
          onClick={() => changeStatus(buttonStatus)}
        />
      )

      const showValidationModalButton: (
        buttonStatus: PaymentQueueStatus,
        currentParkedSaleButtonStatus: ParkedSaleButtonStatus,
      ) => JSX.Element = (buttonStatus, currentParkedSaleButtonStatus) => {
        return (
          <Tooltip
            key={buttonStatus}
            title={
              <span>
                Setting a parked sale order to be "Ready for pickup" will prompt barcode validation.
                <br />
                {`Learn more `}
                <Button
                  type='link'
                  href={HelpLinks.DASHBOARD_BARCODE_VALIDATION}
                  target='_blank'
                  style={{ padding: 0 }}
                >
                  {` here.`}
                </Button>
              </span>
            }
          >
            <div>
              <ParkedSaleStatusButton
                status={buttonStatus}
                buttonStatus={currentParkedSaleButtonStatus}
                onClick={() => {
                  setValidationModalOpen(true)
                }}
              />
            </div>
          </Tooltip>
        )
      }

      const showDisabledButton: (
        buttonStatus: PaymentQueueStatus,
        currentParkedSaleButtonStatus: ParkedSaleButtonStatus,
      ) => JSX.Element = (buttonStatus, currentParkedSaleButtonStatus) => {
        return (
          <Tooltip key={buttonStatus} title={<span>Insufficient Permissions</span>}>
            <div>
              <ParkedSaleStatusButton
                status={buttonStatus}
                buttonStatus={currentParkedSaleButtonStatus}
                disabled={!canEditSales}
              />
            </div>
          </Tooltip>
        )
      }

      const showRevertStatusButton: (
        buttonStatus: PaymentQueueStatus,
        currentParkedSaleButtonStatus: ParkedSaleButtonStatus,
      ) => JSX.Element = (buttonStatus, currentParkedSaleButtonStatus) => {
        return (
          <ParkedSaleStatusButton
            key={`key${buttonStatus}`}
            status={buttonStatus}
            buttonStatus={currentParkedSaleButtonStatus}
            onClick={() => {
              if (
                externalSourceName &&
                window.confirm(
                  `Are you sure?\n\nThe order status will not changed on ${externalSourceName}.`,
                )
              ) {
                changeStatus(buttonStatus)
              }
            }}
          />
        )
      }

      return (
        <>
          {getAvailableButtons().map((buttonStatus: PaymentQueueStatus) => {
            const currentParkedSaleButtonStatus = parkedSaleButtonStatus(buttonStatus)

            // Open barcode validation modal to change order status
            if (
              isDashboardBarcodeValidationEnabled &&
              (buttonStatus === PaymentQueueStatus.READY_FOR_PICKUP ||
                buttonStatus === PaymentQueueStatus.OUT_FOR_DELIVERY)
            ) {
              if (canEditSales || !completedPayment) {
                return showValidationModalButton(buttonStatus, currentParkedSaleButtonStatus)
              } else {
                return showDisabledButton(buttonStatus, currentParkedSaleButtonStatus)
              }
            }

            // Show alert when PENDING or READY_FOR_DELIVERY is clicked from OUT_FOR_DELIVERY and READY_FOR_PICKUP
            if (
              externalSourceName &&
              buttonStatus === PaymentQueueStatus.PENDING &&
              (status === PaymentQueueStatus.OUT_FOR_DELIVERY ||
                status === PaymentQueueStatus.READY_FOR_PICKUP)
            ) {
              return showRevertStatusButton(buttonStatus, currentParkedSaleButtonStatus)
            }

            // Disable the complete button for unpaid orders or show warning for paid orders
            if (
              buttonStatus === PaymentQueueStatus.COMPLETED &&
              (status === PaymentQueueStatus.PENDING ||
                status === PaymentQueueStatus.READY_FOR_DELIVERY ||
                !completedPayment)
            ) {
              return showConditionalCompleteButton(
                buttonStatus,
                currentParkedSaleButtonStatus,
                completedPayment,
                parkedSaleModalQueue,
                changeStatus,
              )
            }

            // Default
            return defaultParkedSaleButton(buttonStatus, currentParkedSaleButtonStatus)
          })}
        </>
      )
    },
  ),
)
